import { useMutation } from "@tanstack/react-query";
import { reportService } from "services";
import {
  ServiceRevenueQuery,
  ServiceRevenueResponse,
  S2aQuery,
  S2bQuery,
  S2cQuery,
  S2dQuery,
  S2eQuery,
  S3aQuery,
  S4Query,
  S5Query,
  S6Query,
  S7Query,
  TResponse,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";

export function useReport() {
  const fetchS1aHkd = useMutation<
    ServiceRevenueResponse | null,
    any,
    ServiceRevenueQuery
  >({
    mutationFn: (params: ServiceRevenueQuery) => reportService.s1aHkd(params),
    onError: (err) => {
      SnackbarUtils.error("Tải báo cáo doanh thu dịch vụ thất bại");
      console.error("fetchS1aHkd failed", err);
    },
  });

  const fetchS2aHkd = useMutation<TResponse<any> | null, any, S2aQuery>({
    mutationFn: (params: S2aQuery) => reportService.s2aHkd(params),
    onError: (err) => {
      SnackbarUtils.error("Tải báo cáo doanh thu (mức 2) thất bại");
      console.error("fetchS2aHkd failed", err);
    },
  });

  const fetchS2bHkd = useMutation<TResponse<any> | null, any, S2bQuery>({
    mutationFn: (params: S2bQuery) => reportService.s2bHkd(params),
    onError: (err) => {
      SnackbarUtils.error("Tải báo cáo doanh thu theo ngành nghề thất bại");
      console.error("fetchS2bHkd failed", err);
    },
  });

  const fetchS2cHkd = useMutation<TResponse<any> | null, any, S2cQuery>({
    mutationFn: (params: S2cQuery) => reportService.s2cHkd(params),
    onError: (err) => {
      SnackbarUtils.error("Tải báo cáo doanh thu - chi phí thất bại");
      console.error("fetchS2cHkd failed", err);
    },
  });

  const fetchS2dHkd = useMutation<TResponse<any> | null, any, S2dQuery>({
    mutationFn: (params: S2dQuery) => reportService.s2dHkd(params),
    onError: (err) => {
      SnackbarUtils.error("Tải báo cáo tồn kho thất bại");
      console.error("fetchS2dHkd failed", err);
    },
  });

  const fetchS2eHkd = useMutation<TResponse<any> | null, any, S2eQuery>({
    mutationFn: (params: S2eQuery) => reportService.s2eHkd(params),
    onError: (err) => {
      SnackbarUtils.error("Tải báo cáo chi tiết tiền thất bại");
      console.error("fetchS2eHkd failed", err);
    },
  });

  const fetchS3aHkd = useMutation<TResponse<any> | null, any, S3aQuery>({
    mutationFn: (params: S3aQuery) => reportService.s3aHkd(params),
    onError: (err) => {
      SnackbarUtils.error("Tải báo cáo chi phí SX-KD thất bại");
      console.error("fetchS3aHkd failed", err);
    },
  });

  const fetchS4Hkd = useMutation<TResponse<any> | null, any, S4Query>({
    mutationFn: (params: S4Query) => reportService.s4Hkd(params),
    onError: (err) => {
      SnackbarUtils.error("Tải báo cáo tài sản, nguồn vốn thất bại");
      console.error("fetchS4Hkd failed", err);
    },
  });

  const fetchS5Hkd = useMutation<TResponse<any> | null, any, S5Query>({
    mutationFn: (params: S5Query) => reportService.s5Hkd(params),
    onError: (err) => {
      SnackbarUtils.error("Tải báo cáo nghĩa vụ thuế thất bại");
      console.error("fetchS5Hkd failed", err);
    },
  });

  const fetchS6Hkd = useMutation<TResponse<any> | null, any, S6Query>({
    mutationFn: (params: S6Query) => reportService.s6Hkd(params),
    onError: (err) => {
      SnackbarUtils.error("Tải báo cáo công nợ thất bại");
      console.error("fetchS6Hkd failed", err);
    },
  });

  const fetchS7Hkd = useMutation<TResponse<any> | null, any, S7Query>({
    mutationFn: (params: S7Query) => reportService.s7Hkd(params),
    onError: (err) => {
      SnackbarUtils.error("Tải báo cáo sổ tiền thất bại");
      console.error("fetchS7Hkd failed", err);
    },
  });

  // ============================================================
  // Admin report hooks — Sổ sách, báo cáo quản trị
  // ============================================================

  const fetchRpInOut = useMutation<TResponse<any> | null, any, any>({
    mutationFn: (params: any) => reportService.rpInOut(params),
    onError: (err) => {
      SnackbarUtils.error("Tải báo cáo nhập xuất tồn thất bại");
      console.error("fetchRpInOut failed", err);
    },
  });

  const fetchRpAccountReceivable = useMutation<TResponse<any> | null, any, any>({
    mutationFn: (params: any) => reportService.rpAccountReceivable(params),
    onError: (err) => {
      SnackbarUtils.error("Tải báo cáo tổng hợp công nợ thất bại");
      console.error("fetchRpAccountReceivable failed", err);
    },
  });

  const fetchRpAccountReceivableDetail = useMutation<TResponse<any> | null, any, any>({
    mutationFn: (params: any) => reportService.rpAccountReceivableDetail(params),
    onError: (err) => {
      SnackbarUtils.error("Tải sổ chi tiết công nợ thất bại");
      console.error("fetchRpAccountReceivableDetail failed", err);
    },
  });

  const fetchRpDownGtgt = useMutation<TResponse<any> | null, any, any>({
    mutationFn: (params: any) => reportService.rpDownGtgt(params),
    onError: (err) => {
      SnackbarUtils.error("Tải báo cáo giảm GTGT thất bại");
      console.error("fetchRpDownGtgt failed", err);
    },
  });

  const fetchRpHkdState = useMutation<TResponse<any> | null, any, any>({
    mutationFn: (params: any) => reportService.rpHkdState(params),
    onError: (err) => {
      SnackbarUtils.error("Tải báo cáo tình hình tài chính thất bại");
      console.error("fetchRpHkdState failed", err);
    },
  });

  const fetchRpPurchaseDetail = useMutation<TResponse<any> | null, any, any>({
    mutationFn: (params: any) => reportService.rpPurchaseDetail(params),
    onError: (err) => {
      SnackbarUtils.error("Tải sổ chi tiết mua hàng thất bại");
      console.error("fetchRpPurchaseDetail failed", err);
    },
  });

  return {
    fetchS1aHkd,
    fetchS2aHkd,
    fetchS2bHkd,
    fetchS2cHkd,
    fetchS2dHkd,
    fetchS2eHkd,
    fetchS3aHkd,
    fetchS4Hkd,
    fetchS5Hkd,
    fetchS6Hkd,
    fetchS7Hkd,
    // Admin reports
    fetchRpInOut,
    fetchRpAccountReceivable,
    fetchRpAccountReceivableDetail,
    fetchRpDownGtgt,
    fetchRpHkdState,
    fetchRpPurchaseDetail,
  };
}
